import React, { useEffect } from 'react';
import { Box, Button, Container, Flex, Heading, Separator, Text } from '@radix-ui/themes';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPrint, FaShieldAlt, FaFileContract, FaUserMd, FaExclamationTriangle, FaWifi, FaHeartbeat } from 'react-icons/fa';

const TermsPage = () => {
    const navigate = useNavigate();

    // Scroll to top on mount
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const handlePrint = () => {
        window.print();
    };

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const sections = [
        { id: 'introduction', label: '1. Introduction', icon: <FaFileContract className="text-teal-600" /> },
        { id: 'clinical-use', label: '2. Clinical Disclaimers', icon: <FaHeartbeat className="text-red-500" /> },
        { id: 'authorized-staff', label: '3. Authorized Access', icon: <FaUserMd className="text-blue-500" /> },
        { id: 'connectivity', label: '4. System Dependencies', icon: <FaWifi className="text-amber-500" /> },
        { id: 'data-privacy', label: '5. Privacy & Security', icon: <FaShieldAlt className="text-indigo-500" /> },
        { id: 'liability', label: '6. Limitation of Liability', icon: <FaExclamationTriangle className="text-rose-500" /> },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-tr from-[#f4f7f6] via-[#fafcfb] to-[#e8f1ee] py-8 px-4 sm:px-6 lg:px-8 relative overflow-hidden print:bg-white print:p-0">
            {/* Decorative background blobs - hidden on print */}
            <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] rounded-full bg-teal-50/40 blur-3xl pointer-events-none print:hidden"></div>
            <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full bg-emerald-50/30 blur-3xl pointer-events-none print:hidden"></div>

            <Container size="4" className="relative z-10">
                {/* Upper control bar - hidden on print */}
                <Flex justify="between" align="center" className="mb-8 flex-wrap gap-4 print:hidden animate-fade-in">
                    <Button 
                        variant="ghost" 
                        color="teal" 
                        size="3" 
                        onClick={() => navigate('/auth/login')}
                        className="cursor-pointer hover:translate-x-[-4px] transition-transform duration-200"
                    >
                        <FaArrowLeft className="mr-2 text-sm" /> Back to Login
                    </Button>
                    
                    <Button 
                        variant="soft" 
                        color="gray" 
                        size="3" 
                        onClick={handlePrint}
                        className="cursor-pointer hover:bg-teal-50 hover:text-teal-700 transition-colors"
                    >
                        <FaPrint className="mr-2 text-sm" /> Print Terms
                    </Button>
                </Flex>

                {/* Document Header */}
                <div className="text-center mb-12 print:mb-6 animate-fade-in-up">
                    <div className="inline-flex items-center justify-center p-3 bg-teal-50 text-teal-600 rounded-2xl mb-4 print:hidden shadow-sm">
                        <FaShieldAlt className="text-4xl" />
                    </div>
                    <Heading size="8" className="tracking-tight text-gray-900 font-extrabold print:text-2xl">
                        Terms of Service
                    </Heading>
                    <Text size="3" color="gray" className="mt-2 block font-medium print:text-xs">
                        Intelligent Monitoring & Alarm Control System (IMACS)
                    </Text>
                    <Text size="1" color="gray" className="mt-1 block opacity-75 print:hidden">
                        Last Updated: May 2026
                    </Text>
                </div>

                <Separator size="4" className="mb-10 opacity-30 print:mb-6" />

                {/* Main Content Layout */}
                <Flex direction={{ initial: 'column', md: 'row' }} gap="8" className="items-start">
                    {/* Left Sidebar - Table of Contents (Sticky on Desktop) - hidden on print */}
                    <Box 
                        className="w-full md:w-[280px] sticky top-8 bg-white/70 backdrop-blur border border-white/40 shadow-lg rounded-2xl p-5 print:hidden hidden md:block animate-fade-in-left"
                    >
                        <Text size="3" className="font-semibold text-gray-800 uppercase tracking-wider mb-4 block">
                            Navigation
                        </Text>
                        <Flex direction="column" gap="1">
                            {sections.map((sec) => (
                                <button
                                    key={sec.id}
                                    onClick={() => scrollToSection(sec.id)}
                                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-[14px] text-gray-600 hover:text-teal-700 hover:bg-teal-50/50 transition-all font-medium border border-transparent hover:border-teal-100/50 cursor-pointer"
                                >
                                    {sec.icon}
                                    {sec.label}
                                </button>
                            ))}
                        </Flex>
                        
                        <Separator size="4" className="my-4 opacity-50" />
                        <Text size="1" color="gray" className="leading-relaxed block">
                            Need help? <a href="https://www.lukahealthcare.com/contact/" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">Contact Support</a>
                        </Text>
                    </Box>

                    {/* Right Panel - Terms Document */}
                    <Box 
                        className="flex-1 w-full bg-white/80 backdrop-blur border border-white/50 shadow-xl rounded-3xl p-6 sm:p-10 print:shadow-none print:bg-transparent print:p-0 print:border-none animate-fade-in-right"
                    >
                        <div className="space-y-10 print:space-y-6">
                            
                            {/* Section 1 */}
                            <section id="introduction" className="scroll-mt-6">
                                <Flex align="center" gap="3" className="mb-4">
                                    <div className="p-2 bg-teal-50 rounded-xl text-teal-600 print:hidden">
                                        <FaFileContract className="text-lg" />
                                    </div>
                                    <Heading size="5" className="text-gray-900 font-bold">1. Agreement to Terms</Heading>
                                </Flex>
                                <Text size="3" className="text-gray-600 leading-relaxed block">
                                    These Terms of Service ("Terms") govern your access to and use of the Intelligent Monitoring and Alarm Control System ("IMACS" or the "Service"), a clinical alert utility developed and managed by <strong>Luka Healthcare Pvt. Ltd.</strong> ("Luka Healthcare", "we", "us", or "our"). 
                                </Text>
                                <Text size="3" className="text-gray-600 leading-relaxed block mt-3">
                                    By logging into or accessing the Service, you represent that you are an authorized healthcare practitioner or staff member of an institution holding a valid licensing agreement, and you agree to adhere to these terms. If you do not agree, you must immediately cease all use of the Service.
                                </Text>
                            </section>

                            <Separator size="4" className="opacity-20" />

                            {/* Section 2 */}
                            <section id="clinical-use" className="scroll-mt-6">
                                <Flex align="center" gap="3" className="mb-4">
                                    <div className="p-2 bg-red-50 rounded-xl text-red-500 print:hidden">
                                        <FaHeartbeat className="text-lg" />
                                    </div>
                                    <Heading size="5" className="text-gray-900 font-bold">2. Clinical Disclaimer & Auxiliary Alarm Status</Heading>
                                </Flex>
                                
                                <div className="bg-rose-50/50 border border-rose-100 rounded-2xl p-4 sm:p-5 mb-4 print:bg-transparent print:border-l-4 print:border-l-gray-400 print:p-2">
                                    <Flex gap="3" align="start">
                                        <FaExclamationTriangle className="text-rose-500 text-lg mt-0.5 shrink-0 print:hidden" />
                                        <div>
                                            <Text size="2" className="font-semibold text-rose-950 block">CRITICAL REGULATORY NOTICE</Text>
                                            <Text size="2" className="text-rose-800 leading-relaxed mt-1 block print:text-gray-700">
                                                IMACS IS AN AUXILIARY AND SECONDARY ALARM ALERTS NOTIFICATION SYSTEM. IT IS DESIGNED TO ASSIST CAREGIVERS BY STREAMLINING PATIENT MONITORING ALERTS. IT IS NOT A PRIMARY MEDICAL TELEMETRY OR LIFE-SUPPORT SAFETY MONITOR.
                                            </Text>
                                        </div>
                                    </Flex>
                                </div>

                                <Text size="3" className="text-gray-600 leading-relaxed block">
                                    <strong>No Substitute for Direct Observation:</strong> The Service is not a substitute for standard nursing clinical procedures, direct patient visualization, periodic manual rounds, or primary medical-grade alarm telemetry.
                                </Text>
                                <Text size="3" className="text-gray-600 leading-relaxed block mt-3">
                                    <strong>Independent Clinical Judgment:</strong> Caregivers must rely entirely on their own professional, independent clinical judgment when diagnosing, caring for, or responding to patients. Luka Healthcare does not monitor patients directly and does not dictate clinical treatment.
                                </Text>
                            </section>

                            <Separator size="4" className="opacity-20" />

                            {/* Section 3 */}
                            <section id="authorized-staff" className="scroll-mt-6">
                                <Flex align="center" gap="3" className="mb-4">
                                    <div className="p-2 bg-blue-50 rounded-xl text-blue-500 print:hidden">
                                        <FaUserMd className="text-lg" />
                                    </div>
                                    <Heading size="5" className="text-gray-900 font-bold">3. Authorized Personnel Only</Heading>
                                </Flex>
                                <Text size="3" className="text-gray-600 leading-relaxed block">
                                    Access to IMACS is strictly limited to hospital staff, clinical practitioners, security officers, and administrators who have been explicitly provisioned with account credentials by their institution.
                                </Text>
                                <Text size="3" className="text-gray-600 leading-relaxed block mt-3">
                                    You are responsible for keeping your login credentials confidential. You must immediately notify hospital administration if you suspect unauthorized access or compromise of your credentials. Sharing credentials with patient relatives, external third-parties, or unauthorized employees is strictly prohibited.
                                </Text>
                            </section>

                            <Separator size="4" className="opacity-20" />

                            {/* Section 4 */}
                            <section id="connectivity" className="scroll-mt-6">
                                <Flex align="center" gap="3" className="mb-4">
                                    <div className="p-2 bg-amber-50 rounded-xl text-amber-500 print:hidden">
                                        <FaWifi className="text-lg" />
                                    </div>
                                    <Heading size="5" className="text-gray-900 font-bold">4. Network, Connectivity & Device Dependencies</Heading>
                                </Flex>
                                <Text size="3" className="text-gray-600 leading-relaxed block">
                                    The correct operation of IMACS relies on constant hardware and software dependencies:
                                </Text>
                                <ul className="list-disc pl-5 mt-3 space-y-2 text-gray-600 text-[14px]">
                                    <li>Continuous local hospital Wi-Fi, Ethernet, and Internet bandwidth.</li>
                                    <li>Properly configured and powered Sensor Gateway devices.</li>
                                    <li>Operational bed sensors, patient monitoring accessories, and healthy battery status.</li>
                                </ul>
                                <Text size="3" className="text-gray-600 leading-relaxed block mt-3">
                                    Luka Healthcare is not responsible for delayed, missed, or corrupted alarms resulting from network dropouts, power failures, incorrect hardware placement, gateway disconnection, or local interference.
                                </Text>
                            </section>

                            <Separator size="4" className="opacity-20" />

                            {/* Section 5 */}
                            <section id="data-privacy" className="scroll-mt-6">
                                <Flex align="center" gap="3" className="mb-4">
                                    <div className="p-2 bg-indigo-50 rounded-xl text-indigo-500 print:hidden">
                                        <FaShieldAlt className="text-lg" />
                                    </div>
                                    <Heading size="5" className="text-gray-900 font-bold">5. Patient Data Privacy & HIPAA Alignment</Heading>
                                </Flex>
                                <Text size="3" className="text-gray-600 leading-relaxed block">
                                    IMACS processes telemetry and clinical log metadata. We are committed to complying with relevant healthcare data security standards, HIPAA directives, and state/country regulations governing Protected Health Information (PHI).
                                </Text>
                                <Text size="3" className="text-gray-600 leading-relaxed block mt-3">
                                    We employ secure transport layers, encryption at rest, and role-based access control. As a clinical operator, you must comply with your institution's specific internal security guidelines and patient privacy rules.
                                </Text>
                            </section>

                            <Separator size="4" className="opacity-20" />

                            {/* Section 6 */}
                            <section id="liability" className="scroll-mt-6">
                                <Flex align="center" gap="3" className="mb-4">
                                    <div className="p-2 bg-rose-50 rounded-xl text-rose-500 print:hidden">
                                        <FaExclamationTriangle className="text-lg" />
                                    </div>
                                    <Heading size="5" className="text-gray-900 font-bold">6. Limitation of Liability</Heading>
                                </Flex>
                                <Text size="3" className="text-gray-600 leading-relaxed block">
                                    TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, LUKA HEALTHCARE PVT. LTD. SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF LIFE, PHYSICAL INJURY, BUSINESS DOWNTIME, AND ALARM FAULT SYSTEM DISRUPTION, ARISING OUT OF OR IN RELATION TO THE SOFTWARE USE.
                                </Text>
                                <Text size="3" className="text-gray-600 leading-relaxed block mt-3">
                                    Luka Healthcare's total liability under licensing terms is strictly capped at the license fees paid by your institution in the twelve (12) months preceding the claim event.
                                </Text>
                            </section>

                            <Separator size="4" className="opacity-20 print:hidden" />

                            {/* Bottom CTA - hidden on print */}
                            <div className="bg-teal-50/50 rounded-2xl p-6 text-center border border-teal-100/50 print:hidden">
                                <Text size="3" className="font-semibold text-teal-950 block">Acceptance of Terms</Text>
                                <Text size="2" color="gray" className="mt-1 block max-w-lg mx-auto">
                                    By clicking "Back to Login" and continuing to use the software, you affirm your understanding and adherence to these clinical terms of service.
                                </Text>
                                <Button 
                                    color="teal" 
                                    size="3" 
                                    className="mt-4 cursor-pointer hover:scale-[1.02] active:scale-[0.98] transition-transform font-semibold"
                                    onClick={() => navigate('/auth/login')}
                                >
                                    Proceed to Login
                                </Button>
                            </div>
                        </div>
                    </Box>
                </Flex>
            </Container>

            {/* Print Layout Styling */}
            <style jsx="true">{`
                @media print {
                    .min-h-screen {
                        min-height: auto !important;
                        background: none !important;
                    }
                    body {
                        color: #111 !important;
                        background-color: #fff !important;
                    }
                    section {
                        page-break-inside: avoid;
                    }
                }
                
                /* Micro-animations */
                .animate-fade-in {
                    animation: fadeIn 0.4s ease-out forwards;
                }
                .animate-fade-in-up {
                    animation: fadeInUp 0.5s ease-out forwards;
                }
                .animate-fade-in-left {
                    animation: fadeInLeft 0.5s ease-out 0.1s forwards;
                    opacity: 0;
                }
                .animate-fade-in-right {
                    animation: fadeInRight 0.5s ease-out 0.1s forwards;
                    opacity: 0;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(15px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes fadeInLeft {
                    from { opacity: 0; transform: translateX(-15px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes fadeInRight {
                    from { opacity: 0; transform: translateX(15px); }
                    to { opacity: 1; transform: translateX(0); }
                }
            `}</style>
        </div>
    );
};

export default TermsPage;
